/**
 * The camera connectivity result (ENG-2457): what works, what doesn't, why,
 * how to fix it and whose fix it is. Not red/green: a summary line, one
 * finding card per group of cameras that stopped at the same step (the same
 * card the Troubleshooter uses), and a table with each camera's step ladder.
 *
 * Renders a result from either source — the connector's own report or a full
 * camera test — and does no fetching itself.
 */
import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FindingCard } from "../../Troubleshooting/tabs/DiagnosisTab";
import { CameraLink } from "../../Troubleshooting/TroubleshootingUi";
import {
  SMTP_WAITING_STEPS,
  SMTP_WAITING_TITLE,
  STEP_STATE_TEXT,
  adviceForCamera,
  cameraReported,
  cameraStatusText,
  dryRunSupported,
  familyConnectionLabel,
  familyOf,
  isSmtpWaiting,
  stepColumns,
  stepLabelFor,
  filterResult,
  findingReport,
  formatClock,
  groupsToFindings,
  ladderFor,
  reasonForCamera,
  runProgress,
  runProgressSentence,
  sourceDescription,
  summaryDetails,
  summarySentence,
  summaryTone,
  SummaryTone,
  videoSummary,
} from "./connectivityModel";
import {
  CameraConnectivity,
  CameraConnectivityStatus,
  ConnectivityResult,
  IntegrationFamily,
  StepId,
  StepState,
} from "./siteConnectivityTypes";
import { CameraMediaThumb, hasMedia } from "./CameraMediaView";

const STEP_ICON: Record<StepState, React.ReactNode> = {
  pass: <CheckCircleIcon sx={{ fontSize: 16, color: "#1e7d34" }} aria-hidden />,
  fail: <CancelIcon sx={{ fontSize: 16, color: "#b3261e" }} aria-hidden />,
  not_reached: (
    <RemoveCircleOutlineIcon
      sx={{ fontSize: 16, color: "#6b7075" }}
      aria-hidden
    />
  ),
  unknown: (
    <HelpOutlineIcon sx={{ fontSize: 16, color: "#6b7075" }} aria-hidden />
  ),
};

const TONE_CLASSES: Record<SummaryTone, string> = {
  bad: "border-[#b3261e] bg-[#fdecec]",
  warn: "border-actuate-orange bg-[#fff4e5]",
  ok: "border-[#1e7d34] bg-[#e6f4ea]",
  info: "border-[#283e6d] bg-[#eaf1fb]",
};

/** A test still running: no verdict colours yet. */
const RUNNING_CLASSES = "border-[#9aa0a6] bg-[#f6f7f8]";

/** A camera the running test hasn't heard back from. */
const TestingStatus = () => (
  <span
    className="font-normal italic text-[#6b7075]"
    data-testid="camera-status-testing"
  >
    testing…
  </span>
);

const STATUS_CLASSES: Record<CameraConnectivityStatus, string> = {
  streaming: "text-[#1e7d34]",
  not_streaming: "text-[#b3261e]",
  unknown: "text-[#5f6368]",
  unproven: "text-[#283e6d]",
  waiting: "text-[#283e6d]",
};

/** What "nothing works" means for the family, in the 0-of-N warning. */
const NOTHING_WORKS: Record<IntegrationFamily, string> = {
  direct: "No camera at this site is sending video",
  vms: "No camera at this site is sending video",
  cloud: "No camera at this site is sending video",
  smtp: "No camera at this site is sending images",
  events: "No camera at this site is sending events",
};

/** One camera's checks, in order. Shape + words, never colour alone. */
export const StepLadder = ({
  camera,
  columns,
}: {
  camera: Pick<CameraConnectivity, "steps" | "camera_id" | "connection">;
  /** The site's steps in order; the camera's own order when absent. */
  columns?: StepId[];
}) => (
  <ol
    className="flex flex-wrap gap-x-3 gap-y-1 m-0 p-0 list-none"
    aria-label="Check steps"
    data-testid={`ladder-${camera.camera_id}`}
  >
    {ladderFor(camera, columns).map((step) => (
      <li
        key={step.id}
        className={`flex items-center gap-1 text-[12px] font-mulish whitespace-nowrap ${
          step.state === "fail"
            ? "font-bold text-[#b3261e]"
            : step.state === "pass"
            ? "text-actuate-blue"
            : "text-[#6b7075]"
        }`}
        data-testid={`step-${camera.camera_id}-${step.id}`}
        data-state={step.state}
        title={`${stepLabelFor(step.id, camera.connection)}: ${
          STEP_STATE_TEXT[step.state]
        }`}
      >
        {STEP_ICON[step.state]}
        <span>{stepLabelFor(step.id, camera.connection)}</span>
        <span className="sr-only">: {STEP_STATE_TEXT[step.state]}</span>
        {step.state === "fail" && <span aria-hidden>(failed)</span>}
      </li>
    ))}
  </ol>
);

const Legend = () => (
  <div
    className="flex flex-wrap gap-4 text-[12px] font-mulish text-[#4c5257]"
    aria-hidden
  >
    {(["pass", "fail", "not_reached", "unknown"] as StepState[]).map(
      (state) => (
        <span key={state} className="flex items-center gap-1">
          {STEP_ICON[state]} {STEP_STATE_TEXT[state]}
        </span>
      )
    )}
  </div>
);

export const ConnectivitySummaryLine = ({
  result,
  prominent = false,
  running = false,
}: {
  result: ConnectivityResult;
  prominent?: boolean;
  /**
   * A full test is still running: say how far it has got, neutrally. The
   * counts so far aren't a verdict, so no red/amber until it's done.
   */
  running?: boolean;
}) => {
  const tone = summaryTone(result.summary);
  const family = familyOf(result);
  const details = running ? [] : summaryDetails(result.summary, family);
  return (
    <div
      className={`border-l-4 rounded px-4 py-3 ${
        running ? RUNNING_CLASSES : TONE_CLASSES[tone]
      }`}
      data-testid="connectivity-summary"
      data-tone={running ? "running" : tone}
    >
      <p
        className="m-0 mb-1 font-mulish text-[12px] font-bold uppercase tracking-wide text-[#4c5257]"
        data-testid="connectivity-family"
      >
        {familyConnectionLabel(result)}
      </p>
      <p
        className={`m-0 font-mulish font-extrabold text-actuate-blue ${
          prominent ? "text-[22px]" : "text-[18px]"
        }`}
      >
        {running
          ? runProgressSentence(runProgress(result), family)
          : summarySentence(result.summary, family)}
      </p>
      {details.length > 0 && (
        <p className="m-0 mt-1 font-mulish text-[14px] text-[#4c5257]">
          {details.join(" · ")}
        </p>
      )}
      {result.detail ? (
        <p
          className="m-0 mt-2 rounded bg-white/70 px-3 py-2 font-mulish text-[14px] text-actuate-blue"
          data-testid="connectivity-detail"
        >
          {result.detail}
        </p>
      ) : null}
      {tone === "bad" && !running && (
        <p className="m-0 mt-2 font-mulish text-[14px] font-bold text-[#b3261e]">
          {NOTHING_WORKS[family]}, so nothing will be detected until this is
          fixed — even though the site shows as active.
        </p>
      )}
      <p className="m-0 mt-2 font-mulish text-[13px] text-[#6b7075]">
        {sourceDescription(result)}
        {result.checked_at ? ` Checked ${formatClock(result.checked_at)}.` : ""}
      </p>
    </div>
  );
};

const copyText = (text: string) => {
  try {
    navigator.clipboard?.writeText(text);
  } catch {
    // Clipboard blocked: nothing else to do.
  }
};

export const SiteConnectivityPanel = ({
  result: fullResult,
  siteId,
  cameraIds,
  prominent = false,
  compact = false,
  running = false,
  cameraAction,
}: {
  result: ConnectivityResult;
  siteId: number;
  /** A full test is still running: see ConnectivitySummaryLine. */
  running?: boolean;
  /**
   * Shown under each camera's name, for video sites only: the
   * Troubleshooter's "Test stream" (ENG-2470).
   */
  cameraAction?: (camera: CameraConnectivity) => React.ReactNode;
  /** Narrow to these cameras (the camera page shows one). */
  cameraIds?: number[];
  /** Larger summary, for onboarding and the 0-streaming case. */
  prominent?: boolean;
  /**
   * For a narrow column (the Troubleshooter's Diagnosis tab): Why, What to
   * do and Video go on a second row under each camera instead of their own
   * columns, so the table fits without scrolling sideways.
   */
  compact?: boolean;
}) => {
  const result = filterResult(fullResult, cameraIds);
  const cameras = result.cameras ?? [];
  const family = familyOf(result);
  const columns = stepColumns(result);
  const showVideo = dryRunSupported(family);
  const smtpWaiting = isSmtpWaiting(result);
  // While an email site waits for its first email, the waiting note below
  // says it once; a card per camera group would repeat it.
  const reportedIds = new Set(
    cameras.filter(cameraReported).map((c) => c.camera_id)
  );
  // While a test runs, cameras it hasn't heard from yet aren't a finding.
  const findings = groupsToFindings(result, siteId).filter(
    (finding) =>
      !(smtpWaiting && finding.title === SMTP_WAITING_TITLE) &&
      !(
        running &&
        (finding.probeCameraIds ?? []).length > 0 &&
        (finding.probeCameraIds ?? []).every((id) => !reportedIds.has(id))
      )
  );
  // Only once some camera has a snapshot or clip (newer admin, full test).
  const showMedia = showVideo && cameras.some((c) => hasMedia(c));
  const nameOf = new Map(cameras.map((c) => [c.camera_id, c.camera_name]));

  if (cameras.length === 0 && result.summary.total === 0) {
    return (
      <div
        className="font-mulish text-[14px] text-[#4c5257] py-4"
        data-testid="connectivity-empty"
      >
        {cameraIds?.length
          ? "There's no result for this camera yet."
          : "There are no cameras at this site to check yet."}
        {result.detail ? (
          <p className="m-0 mt-2" data-testid="connectivity-detail">
            {result.detail}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="connectivity-panel">
      <ConnectivitySummaryLine
        result={result}
        prominent={prominent}
        running={running}
      />

      {smtpWaiting && (
        <section
          className="rounded-md border border-[#283e6d] bg-[#eaf1fb] px-4 py-3"
          aria-label={SMTP_WAITING_TITLE}
          data-testid="smtp-waiting"
        >
          <p className="m-0 font-mulish font-extrabold text-[16px] text-actuate-blue">
            {SMTP_WAITING_TITLE}
          </p>
          <p className="m-0 mt-1 font-mulish text-[14px] text-[#4c5257]">
            Nothing is wrong yet: this site works by email, and no email has
            arrived since it was set up. While you wait, check:
          </p>
          <ol className="m-0 mt-2 pl-5 font-mulish text-[14px] text-actuate-blue">
            {SMTP_WAITING_STEPS.map((step) => (
              <li key={step} className="my-1">
                {step}
              </li>
            ))}
          </ol>
        </section>
      )}

      {findings.length > 0 && (
        <section aria-label="What to fix">
          <h3 className="m-0 mb-2 font-mulish font-extrabold text-[16px] text-actuate-blue">
            What to fix
          </h3>
          <div className="border border-[#d1d5db] rounded-md overflow-hidden bg-white">
            {findings.map((finding) => (
              <div key={finding.id} data-testid="connectivity-group">
                <FindingCard
                  finding={finding}
                  isSample={false}
                  onCopy={() => copyText(findingReport(finding, cameras))}
                />
                {(finding.probeCameraIds?.length ?? 0) > 0 && (
                  <p className="m-0 px-5 pb-4 pl-[76px] bg-[#fffaf5] font-mulish text-[13px] text-[#4c5257]">
                    <span className="font-bold">Cameras: </span>
                    {(finding.probeCameraIds ?? []).map((id, index) => (
                      <React.Fragment key={id}>
                        {index > 0 && ", "}
                        <CameraLink siteId={siteId} cameraId={id}>
                          {nameOf.get(id) ?? `Camera ${id}`}
                        </CameraLink>
                      </React.Fragment>
                    ))}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section aria-label="Each camera">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="m-0 font-mulish font-extrabold text-[16px] text-actuate-blue">
            Each camera
          </h3>
          <Legend />
        </div>
        <div className="overflow-x-auto border border-[#d1d5db] rounded-md bg-white">
          <table className="w-full border-collapse font-mulish text-[13px] text-actuate-blue">
            <thead>
              <tr className="text-left text-[12px] text-[#6b7075]">
                <th className="px-3 py-2 font-bold">Camera</th>
                {showMedia && <th className="px-3 py-2 font-bold">Picture</th>}
                <th className="px-3 py-2 font-bold">Status</th>
                <th className="px-3 py-2 font-bold">Checks</th>
                {!compact && (
                  <>
                    <th className="px-3 py-2 font-bold">Why</th>
                    <th className="px-3 py-2 font-bold">What to do</th>
                    {showVideo && (
                      <th className="px-3 py-2 font-bold">Video</th>
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {cameras.map((camera) => {
                const pending = running && !reportedIds.has(camera.camera_id);
                const reason = pending
                  ? null
                  : reasonForCamera(camera, result.groups ?? []);
                const advice =
                  camera.status === "not_streaming"
                    ? adviceForCamera(camera, result.groups ?? [])
                    : null;
                const detail =
                  camera.detail && camera.status !== "streaming" ? (
                    <div className="mt-1 font-mono text-[11px] text-[#6b7075] break-words">
                      {camera.detail}
                    </div>
                  ) : null;
                const video = showVideo ? videoSummary(camera) : null;
                const hasSecondRow = Boolean(
                  reason || advice || detail || video
                );
                return (
                  <React.Fragment key={camera.camera_id}>
                    <tr
                      className="border-t border-[#d1d5db] align-top"
                      data-testid={`camera-row-${camera.camera_id}`}
                    >
                      <td className="px-3 py-2 font-bold break-words max-w-[220px]">
                        <CameraLink siteId={siteId} cameraId={camera.camera_id}>
                          {camera.camera_name}
                        </CameraLink>
                        {showVideo && cameraAction && (
                          <div className="mt-1">{cameraAction(camera)}</div>
                        )}
                      </td>
                      {showMedia && (
                        <td className="px-3 py-2">
                          {hasMedia(camera) ? (
                            <CameraMediaThumb
                              cameraId={camera.camera_id}
                              cameraName={camera.camera_name}
                              media={camera}
                              facts={camera}
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      <td
                        className={`px-3 py-2 font-bold whitespace-nowrap ${
                          pending ? "" : STATUS_CLASSES[camera.status] ?? ""
                        }`}
                        data-testid={`camera-status-${camera.camera_id}`}
                      >
                        {pending ? (
                          <TestingStatus />
                        ) : (
                          cameraStatusText(camera.status, family)
                        )}
                      </td>
                      <td
                        className={`px-3 py-2 ${
                          compact ? "" : "min-w-[260px]"
                        }`}
                      >
                        <StepLadder camera={camera} columns={columns} />
                      </td>
                      {!compact && (
                        <>
                          <td className="px-3 py-2 max-w-[280px]">
                            {reason ?? "—"}
                            {detail}
                          </td>
                          <td className="px-3 py-2 max-w-[260px]">
                            {advice ?? "—"}
                          </td>
                          {showVideo && (
                            <td className="px-3 py-2 whitespace-nowrap">
                              {video ?? "—"}
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                    {compact && hasSecondRow && (
                      <tr
                        className="align-top"
                        data-testid={`camera-detail-row-${camera.camera_id}`}
                      >
                        <td
                          colSpan={showMedia ? 4 : 3}
                          className="px-3 pb-2 pt-0 break-words"
                        >
                          {(reason || detail) && (
                            <div>
                              <span className="font-bold">Why: </span>
                              {reason ?? "—"}
                              {detail}
                            </div>
                          )}
                          {advice && (
                            <div className="mt-1">
                              <span className="font-bold">What to do: </span>
                              {advice}
                            </div>
                          )}
                          {video && (
                            <div className="mt-1 text-[#4c5257]">
                              <span className="font-bold">Video: </span>
                              {video}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
